import AIRequester from '@cobliteam/api-lib/src/dashboard/ai/AIRequester';
import Environment from '@cobliteam/react-environments';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const scoreRequestSchema = z.object({
  name: z.string().optional().default('user-feedback'),
  value: z.union([z.string(), z.number()]).optional(),
  dataType: z.enum(['NUMERIC', 'CATEGORICAL', 'BOOLEAN']).optional().default('CATEGORICAL'),
  comment: z.string().optional(),
  traceId: z.string().optional(),
  sessionId: z.string().optional(),
});

const scoreResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  value: z.number(),
  comment: z.string().optional(),
  traceId: z.string().optional(),
});

export type ScoreRequest = z.infer<typeof scoreRequestSchema>;
export type ScoreResponse = z.infer<typeof scoreResponseSchema>;

/**
 * Hook to send score feedback to Langfuse via Mastra API
 * @returns Mutation function to send score
 */
export const useSendLangfuseScore = () => {
  return useMutation({
    mutationKey: ['langfuse', 'score'],
    mutationFn: async (scoreData: ScoreRequest) => {
      const environment = Environment.getInstance().getEnvironment();
      const aiRequester = new AIRequester({ host: environment.mastraApi });
      const validatedData = scoreRequestSchema.parse(scoreData);
      const response = await aiRequester.sendLangfuseScore(validatedData);
      return response;
    },
  });
};

/**
 * Hook to get score feedback from Langfuse via Mastra API
 * @param traceId - The trace ID to get scores for
 * @returns Query result with scores
 */
export const useGetLangfuseScores = (traceId?: string) => {
  return useQuery({
    queryKey: ['langfuse', 'score', traceId],
    queryFn: async () => {
      if (!traceId) return null;
      const environment = Environment.getInstance().getEnvironment();
      const aiRequester = new AIRequester({ host: environment.mastraApi });
      const scores = await aiRequester.getLangfuseScoresByTraceId(traceId);
      return scores;
    },
    enabled: false,
  });
};
